import { useTranslation } from 'react-i18next';
import Spinner from './spinner';
import css from './loading-wrapper.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import React, { ReactNode, useEffect, useState } from 'react';

/**
 * A component that you can use if you'd like to have some lazy loading logic with a spinner.
 * This function supports returning a promise, in which case the contents will not be shown until the promise succeeds.
 */
export function LoadingWrapper({
  onLoad,
  children,
}: {
  onLoad?: Function;
  children?: ReactNode;
}) {
  const onLoadIsPresent = onLoad !== undefined;
  const [isLoaded, setIsLoaded] = useState(!onLoadIsPresent); // when onLoaded is not present, no spinner will be shown
  const [isLoadError, setIsLoadError] = useState(false);
  useEffect(() => {
    if (!isLoaded && onLoadIsPresent) {
      const onLoadResults = onLoad();
      if (onLoadResults && onLoadResults.then) {
        // promise, wait for results
        onLoadResults.then(
          () => {
            setIsLoaded(true);
            setIsLoadError(false);
          },
          () => {
            setIsLoaded(true);
            setIsLoadError(true);
          }
        );
      } else {
        // returned data directly, don't worry about it
        setIsLoaded(true);
        setIsLoadError(false);
      }
    }
  }, [isLoaded, onLoad, onLoadIsPresent]);
  if (!isLoaded) {
    return <SpinnerWithMessage />;
  } else if (isLoadError) {
    return <LoadingErrorMessage />;
  } else {
    return <div>{children}</div>;
  }
}

/**
 * Loading sub-component, like a spinner.
 */
function SpinnerWithMessage() {
  const { t } = useTranslation();
  return (
    <div>
      <Spinner visible={true} size={'lg'} />
      <span className={css['loading-message']}>{t('loading.message')}</span>
    </div>
  );
}

/**
 * Loading error sub-component, like an error message.
 */
function LoadingErrorMessage() {
  const { t } = useTranslation();
  return (
    <div className={css['error-container']}>
      <div>
        <FontAwesomeIcon icon={faTimes} className={css['error']} />
      </div>
      <span className={css['loading-message']}>{t('loading.error')}</span>
    </div>
  );
}
